<template>
    <div class="space-y-1 py-2 text-xs text-white">
        <p class="text-gray-300">
            Primary Entity:
            <button v-if="primary.node" class="font-mono text-white hover:text-ui-active" title="Select the entity node"
                @click="select(primary.node.id)">{{ primary.entityId || '(no entity selected)' }}</button>
            <span v-else-if="inherited" class="font-mono text-gray-200" title="Inherited from a parent object">{{ inherited }}</span>
            <span v-else>none</span>
        </p>
        <p v-if="!items.length" class="text-gray-300">No bindings or triggers connected to this object.</p>
        <button v-for="item in items" :key="item.node.id" type="button"
            class="flex w-full items-center justify-between gap-2 rounded border border-white/20 bg-white/5 px-1.5 py-0.5 text-left hover:border-ui-active"
            :title="item.view.summary" @click="select(item.node.id)">
            <span class="truncate">{{ item.view.icon }} {{ item.view.title }} <span class="text-gray-400">{{ item.view.summary }}</span></span>
            <span v-if="item.runs !== null" class="shrink-0 text-gray-300">{{ item.runs }} run{{ item.runs === 1 ? '' : 's' }}</span>
        </button>
        <p v-if="legacyRules" class="text-amber-300">{{ legacyRules }} legacy popup rule{{ legacyRules === 1 ? '' : 's' }} (read only)</p>
        <div class="flex flex-wrap gap-1 pt-1">
            <form-button color="theme" width="auto" height="26px" title="Add a binding that drives a property of this object"
                @click="add('binding:state')">Add binding</form-button>
            <form-button color="theme" width="auto" height="26px" title="Add a trigger on an event of this object"
                @click="add('trigger')">Add trigger</form-button>
            <form-button color="theme" width="auto" height="26px" title="Show the Home Assistant flows on the canvas (Shift+H)"
                @click="hass.setVisible(true)">Show flows</form-button>
        </div>
    </div>
</template>

<script setup>
import { computed, toRaw } from 'vue';
import { useHassFlowStore } from '../../stores/hassFlowStore.js';
import { useUndoStore } from '../../history/undoStore.js';
import { findNode } from '../../graph/graphModel.js';
import { getObjectBindingNodes, getObjectTriggers, getRunWires, getWiresInto } from '../../hass/hassGraph.js';
import { getEffectiveHassConfig } from '../../hass/hassEffective.js';
import { describeHassNode } from '../../hass/hassPresentation.js';
import { getEffectivePrimaryEntity } from '../../automation/primaryEntity.js';
import { getObjectPage } from '../../utils/hasputils.js';

/**
 * Replaces the HASSBuilder popup: an object's Home Assistant configuration
 * is a flow on the canvas now, so the properties panel only summarizes and
 * links to its nodes.
 */
const props = defineProps({
    node: { type: Object, required: true },
});
const hass = useHassFlowStore();
const undo = useUndoStore();

const page = computed(() => toRaw(getObjectPage(props.node)) ?? null);
const uuid = computed(() => toRaw(props.node).id());
// page.hass is plain data outside Vue; these counters say when it changed.
const graph = computed(() => {
    hass.revision;
    undo.historyVersion;
    return page.value?.hass ?? null;
});
const primary = computed(() => {
    const wire = graph.value
        ? getWiresInto(graph.value, { object: uuid.value, port: 'in' }).find(item => findNode(graph.value, item.from.node)?.kind === 'entity')
        : null;
    const node = wire ? findNode(graph.value, wire.from.node) : null;
    return { node, entityId: node?.data.entityId ?? '' };
});
const inherited = computed(() => (primary.value.node || !graph.value ? ''
    : getEffectivePrimaryEntity(toRaw(props.node), { configOf: getEffectiveHassConfig })));
const items = computed(() => {
    if (!graph.value) return [];
    const bindings = getObjectBindingNodes(graph.value, uuid.value).map(node => ({ node, view: describeHassNode(node), runs: null }));
    const triggers = getObjectTriggers(graph.value, uuid.value)
        .map(node => ({ node, view: describeHassNode(node), runs: getRunWires(graph.value, node.id).length }));
    return [...bindings, ...triggers];
});
const legacyRules = computed(() => (toRaw(props.node).hass_config?.automation ?? []).filter(model => model._providerExtra).length);

function select(id) {
    hass.setVisible(true);
    hass.select(page.value, 'node', id);
}

function add(entryId) {
    const entry = hass.palette.flatMap(group => group.entries).find(item => item.id === entryId);
    const layer = toRaw(props.node).getStage?.()?.hassLayer;
    const { error } = hass.addPaletteNode(page.value, entry, {
        anchorObject: { uuid: uuid.value, rect: layer.objectPageRect(props.node) },
        plateWidth: toRaw(props.node).getLayer?.()?.pageWidth ?? 0,
    });
    if (error) console.warn(error);
}
</script>
